import React, { useState } from "react";
import { Shield } from 'lucide-react';
import { Link } from "react-router-dom";
import { PATHS } from "../constants/Paths";

// In production, set REACT_APP_REPGUARD_STORE_URL to your Chrome Web Store listing
// e.g. https://chromewebstore.google.com/detail/repguard/...
const storeUrl = process.env.REACT_APP_REPGUARD_STORE_URL;

const devSteps = [
  { num: 1, title: "Open Chrome Extensions", detail: "In Chrome, go to chrome://extensions" },
  { num: 2, title: "Turn on Developer mode", detail: "Toggle Developer mode on in the top-right corner." },
  { num: 3, title: "Load the extension", detail: "Click Load unpacked, then select the RepGuard extension folder (the extension/ directory from this repo)." },
  { num: 4, title: "Configure RepGuard", detail: "Click the RepGuard icon in the toolbar, set your RepCheck backend URL if needed, and log in with your RepCheck account." },
];

export default function RepGuardInstall() {
  const [showDevSteps, setShowDevSteps] = useState(false);

  return (
    <div className="min-h-screen bg-muted py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link to={PATHS.DASHBOARD} className="text-sm text-muted-foreground hover:text-foreground">
            ← Back to Dashboard
          </Link>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-5 border-b border-border bg-amber-50 dark:bg-amber-500/10">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-5 w-5" aria-hidden />
              How to install RepGuard
            </h1>
            <p className="mt-1 text-muted-foreground">
              RepGuard is a browser extension that checks your X drafts before you post.
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            {storeUrl ? (
              <>
                <div className="flex flex-col items-start gap-3">
                  <p className="text-foreground">
                    Install RepGuard from the Chrome Web Store, then sign in with your RepCheck account in the extension.
                  </p>
                  <a
                    href={storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                  >
                    Add RepGuard to Chrome
                  </a>
                </div>
                <p className="text-sm text-muted-foreground">
                  After installing, click the RepGuard icon in your toolbar and log in with your RepCheck credentials.
                </p>
              </>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">
                  Load the extension in development mode (requires the extension source).
                </p>
                <ol className="space-y-6">
                  {devSteps.map(({ num, title, detail }) => (
                    <li key={num} className="flex gap-4">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 font-semibold flex items-center justify-center text-sm">
                        {num}
                      </span>
                      <div>
                        <h2 className="font-semibold text-foreground">{title}</h2>
                        <p className="mt-1 text-sm text-muted-foreground">{detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}

            <div className="pt-2 border-t border-border">
              <button
                type="button"
                onClick={() => setShowDevSteps((s) => !s)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {showDevSteps ? "Hide" : "Show"} developer install (load unpacked)
              </button>
              {showDevSteps && (
                <ol className="mt-4 space-y-3 pl-4 border-l-2 border-border">
                  {devSteps.map(({ num, title, detail }) => (
                    <li key={num} className="text-sm">
                      <span className="font-medium text-foreground">{num}. {title}</span>
                      <p className="text-muted-foreground mt-0.5">{detail}</p>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-muted border-t border-border">
            <p className="text-sm text-muted-foreground">
              Need help?{" "}
              <Link to={PATHS.SUPPORT} className="text-brand-600 hover:text-brand-700 font-medium">
                Contact support
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
